import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Home, Sparkles } from 'lucide-react';
import { useFullscreen } from '@/contexts/FullscreenContext';
import { useAuth } from '@/contexts/AuthContext';
import { getBaseUrl } from '@/services/httpClient';
import { playAudioSafely } from '@/services/iosAudioHelper';

/**
 * JourneyFinal - ÉCRAN FINAL : "Thank you / Prêt à explorer Hearen"
 * 
 * DISCOVER HEAREN - BUILD 50 SPECS (STRICT):
 * - Voix Hera à l'affichage: "Cet endroit est à vous. Il sera là, quand le monde deviendra trop bruyant."
 * - Calm_031.mp3 continue en fond (depuis JourneyIntro)
 * - Ne pas couper le son avant la phrase
 * 
 * P0 FIX - BUILD 66: Music fade + vocal timing
 * - Music fade down -9dB 800ms BEFORE voice begins
 * - Keep music low during speech
 * - 1500ms calm space after voice ends
 * - Music fade up over 1200ms
 * 
 * iOS SAFARI FIX:
 * - Réutilise window.discoverVoiceAudio (déverrouillé au premier tap sur JourneyIntro)
 * - Change le src et appelle play() sur le même objet Audio
 */

// P0 FIX: Convert linear volume (0-1) to dB and back
const linearToDb = (linear) => 20 * Math.log10(Math.max(linear, 0.0001));
const dbToLinear = (db) => Math.pow(10, db / 20);

// P0 FIX: Fade ambient audio to target dB level
const fadeAmbientTo = (targetDb, durationMs, onComplete) => {
  const audio = window.discoverAmbientAudio;
  if (!audio) {
    if (onComplete) onComplete();
    return;
  }
  
  const startVolume = audio.volume;
  const targetVolume = dbToLinear(targetDb);
  const steps = Math.ceil(durationMs / 50); // 50ms per step
  const stepDuration = durationMs / steps;
  let currentStep = 0;
  
  console.log(`[Discover][Final] 🎵 Fading ambient from ${linearToDb(startVolume).toFixed(1)}dB to ${targetDb}dB over ${durationMs}ms`);
  
  const fadeInterval = setInterval(() => {
    currentStep++;
    const progress = currentStep / steps;
    // Smooth easing
    const easedProgress = 1 - Math.pow(1 - progress, 2);
    const newVolume = startVolume + (targetVolume - startVolume) * easedProgress;
    audio.volume = Math.max(0, Math.min(1, newVolume));
    
    if (currentStep >= steps) {
      clearInterval(fadeInterval);
      audio.volume = targetVolume;
      console.log(`[Discover][Final] 🎵 Ambient fade complete: ${linearToDb(audio.volume).toFixed(1)}dB`);
      if (onComplete) onComplete();
    }
  }, stepDuration);
  
  return fadeInterval;
};

const JourneyFinal = () => {
  const navigate = useNavigate();
  const { setIsFullscreen } = useFullscreen();
  const { user } = useAuth();
  const [showOrb, setShowOrb] = useState(false);
  const [showCheck, setShowCheck] = useState(false);
  const [showContent, setShowContent] = useState(false);
  const [showUpsell, setShowUpsell] = useState(false);
  const heraPlayedRef = useRef(false);
  const fadeIntervalsRef = useRef([]);
  
  const isEnglish = localStorage.getItem('i18nextLng')?.includes('en') ||
                    localStorage.getItem('hearen_lang') === 'en';
  
  const isPremium = user?.isPremium || user?.subscription === 'premium' || user?.role === 'admin';

  useEffect(() => {
    setIsFullscreen(true);
    localStorage.setItem('hearen_first_calm_completed', 'true');
    
    const baseUrl = getBaseUrl();
    const lang = isEnglish ? 'en' : 'fr';
    
    // Animation séquentielle
    setTimeout(() => setShowOrb(true), 200);
    setTimeout(() => setShowCheck(true), 500);
    setTimeout(() => setShowContent(true), 1200);
    setTimeout(() => setShowUpsell(true), 1800);
    
    // === P0 FIX: VOIX HERA FINALE avec Music Fade ===
    // Timing: 
    // - T+1500ms: Start music fade down (-9dB over 800ms)
    // - T+2300ms: Start voice playback
    // - Voice ends: Wait 1500ms
    // - Fade music back up over 1200ms
    
    const playHeraFinalWithMusicFade = async () => {
      if (heraPlayedRef.current) return;
      heraPlayedRef.current = true;
      
      const heraUrl = `${baseUrl}/api/voice-files/discover_final_${lang}.mp3`;
      console.log(`[Discover][Final] 🎙️ Playing Hera final with music fade: ${heraUrl}`);
      
      // Store original ambient volume for restoration
      const originalVolume = window.discoverAmbientAudio?.volume || 0.35;
      const originalDb = linearToDb(originalVolume);
      const targetDb = originalDb - 9; // -9 dB reduction
      
      console.log(`[Discover][Final] 🎵 Original ambient: ${originalDb.toFixed(1)}dB, target during voice: ${targetDb.toFixed(1)}dB`);
      
      // Step 1: Fade music DOWN (-9dB over 800ms) BEFORE voice starts
      await new Promise(resolve => {
        const interval = fadeAmbientTo(targetDb, 800, resolve);
        if (interval) fadeIntervalsRef.current.push(interval);
      });
      
      // Step 2: Play Hera voice
      const heraAudio = window.discoverVoiceAudio || new Audio();
      heraAudio.src = heraUrl;
      heraAudio.volume = 0.85; // Slightly higher for clarity
      heraAudio.preload = 'auto';
      
      const success = await playAudioSafely(heraAudio, 'Discover Hera Final');
      
      if (success) {
        console.log('[Discover][Final] ✅ Hera final voice playing');
        
        // Wait for voice to finish + 1500ms calm space
        heraAudio.onended = () => {
          console.log('[Discover][Final] 🎙️ Voice ended, waiting 1500ms calm space...');
          
          setTimeout(() => {
            // Step 3: Fade music back UP over 1200ms
            console.log('[Discover][Final] 🎵 Fading music back up...');
            const interval = fadeAmbientTo(originalDb, 1200);
            if (interval) fadeIntervalsRef.current.push(interval);
          }, 1500);
        };
      } else {
        console.error('[Discover][Final] ❌ Hera final voice blocked, restoring music');
        // Restore music if voice failed
        fadeAmbientTo(originalDb, 500);
      }
    };
    
    // Start the sequence after showContent animation
    // T+1500ms = content visible, then start music fade
    const heraTimer = setTimeout(() => {
      playHeraFinalWithMusicFade();
    }, 1500);
    
    return () => {
      clearTimeout(heraTimer);
      // Clear any active fade intervals
      fadeIntervalsRef.current.forEach(interval => clearInterval(interval));
      fadeIntervalsRef.current = [];
      setIsFullscreen(false);
    };
  }, [setIsFullscreen, isEnglish]);

  const handleGoHome = () => {
    console.log('[Discover][Final] 🏁 Finishing tour, stopping ambient audio');
    
    // Clear any active fades
    fadeIntervalsRef.current.forEach(interval => clearInterval(interval));
    fadeIntervalsRef.current = [];
    
    // Arrêter le son d'ambiance avec fade-out
    if (window.discoverAmbientAudio) {
      const audio = window.discoverAmbientAudio;
      const fadeOutDuration = 800;
      const fadeOutSteps = 16;
      const fadeOutInterval = fadeOutDuration / fadeOutSteps;
      const startVolume = audio.volume;
      let currentStep = 0;
      
      const fadeOut = setInterval(() => {
        currentStep++;
        const newVolume = startVolume * (1 - currentStep / fadeOutSteps);
        audio.volume = Math.max(newVolume, 0);
        
        if (currentStep >= fadeOutSteps) {
          clearInterval(fadeOut);
          audio.pause();
          audio.src = '';
          window.discoverAmbientAudio = null;
          console.log('[Discover][Final] ✅ Ambient audio stopped with fade-out');
        }
      }, fadeOutInterval);
    }
    
    navigate('/journey-orientation');
  };

  const handlePremium = () => {
    console.log('[Discover][Final] 💎 Going to premium page');
    navigate('/premium');
  };

  return (
    <div 
      className="min-h-screen relative overflow-hidden flex flex-col items-center justify-center px-6"
      style={{
        background: 'linear-gradient(135deg, #8b5cf6 0%, #6366f1 50%, #a855f7 100%)',
      }}
    >
      {/* Orbe qui s'ouvre */}
      <div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 transition-all duration-1000"
        style={{
          width: showOrb ? '800px' : '0px',
          height: showOrb ? '800px' : '0px',
          background: 'radial-gradient(circle, rgba(255, 255, 255, 0.3) 0%, rgba(139, 92, 246, 0.15) 40%, transparent 70%)',
          filter: 'blur(80px)',
          opacity: showOrb ? 1 : 0,
        }}
      />

      {/* Halo doux */}
      <div
        className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2"
        style={{
          width: '600px',
          height: '600px',
          background: 'radial-gradient(circle, rgba(167, 139, 250, 0.2) 0%, transparent 70%)',
          filter: 'blur(60px)',
          animation: 'halo-pulse 3s ease-in-out infinite',
          pointerEvents: 'none',
        }}
      />

      <div className="relative z-10 flex flex-col items-center max-w-lg">
        {/* Checkmark */}
        <div 
          className="mb-10 relative"
          style={{
            opacity: showCheck ? 1 : 0,
            transform: showCheck ? 'scale(1)' : 'scale(0.8)',
            transition: 'all 700ms ease-out',
          }}
        >
          <div 
            className="w-32 h-32 rounded-full flex items-center justify-center"
            style={{
              background: 'rgba(255, 255, 255, 0.2)',
              backdropFilter: 'blur(10px)',
              border: '2px solid rgba(255, 255, 255, 0.3)',
              boxShadow: '0 0 40px rgba(255, 255, 255, 0.4), 0 8px 32px rgba(139, 92, 246, 0.5)',
            }}
          >
            <svg 
              width="64" 
              height="64" 
              viewBox="0 0 64 64" 
              fill="none"
              style={{ animation: showCheck ? 'check-stroke-draw 700ms ease-out forwards' : 'none' }}
            >
              <path 
                d="M 16 32 L 28 44 L 48 20" 
                stroke="white" 
                strokeWidth="4" 
                strokeLinecap="round" 
                strokeLinejoin="round"
                strokeDasharray="60"
                strokeDashoffset={showCheck ? "0" : "60"}
                style={{ transition: 'stroke-dashoffset 700ms ease-out' }}
              />
            </svg>
          </div>
          
          {/* Particules */}
          <div className="absolute -top-4 -right-4 w-8 h-8 bg-yellow-300 rounded-full animate-ping opacity-75" />
          <div className="absolute -bottom-4 -left-4 w-6 h-6 bg-pink-300 rounded-full animate-ping opacity-75" style={{ animationDelay: '0.3s' }} />
        </div>

        {/* Contenu */}
        <div
          style={{
            opacity: showContent ? 1 : 0,
            transform: showContent ? 'translateY(0)' : 'translateY(15px)',
            transition: 'all 700ms ease-out',
          }}
        >
          {/* P0 FIX: Text matches Hera's voice script */}
          <h1 className="text-3xl font-bold text-white mb-4 text-center leading-relaxed">
            {isEnglish ? 'This place is yours.' : 'Cet endroit est à vous.'}
          </h1>

          <p className="text-white/90 text-center text-lg mb-8 leading-relaxed">
            {isEnglish
              ? 'It will be here, when the world grows too loud.'
              : 'Il sera là, quand le monde deviendra trop bruyant.'
            }
          </p>

          {/* Upsell Premium - UNIQUEMENT pour FREE */}
          {!isPremium && showUpsell && (
            <div
              className="mb-8 p-6 rounded-2xl"
              style={{
                background: 'rgba(255, 255, 255, 0.1)',
                backdropFilter: 'blur(10px)',
                border: '1px solid rgba(255, 255, 255, 0.2)',
                opacity: showUpsell ? 1 : 0,
                transform: showUpsell ? 'translateY(0)' : 'translateY(10px)',
                transition: 'all 500ms ease-out 200ms',
              }}
            >
              <div className="flex items-center gap-2 justify-center mb-3">
                <Sparkles className="w-5 h-5 text-yellow-300" />
                <p className="text-white/90 text-sm text-center">
                  {isEnglish
                    ? 'Unlock Hera\'s voice and complete sound therapy.'
                    : 'Débloquez la voix Hera et l\'expérience sonore complète.'
                  }
                </p>
              </div>
              <button
                onClick={handlePremium}
                className="w-full py-2 rounded-full text-white/90 text-sm font-medium transition-all hover:bg-white/20"
                style={{ border: '1px solid rgba(255, 255, 255, 0.3)' }}
              >
                {isEnglish ? 'Discover Premium' : 'Découvrir le Premium'}
              </button>
            </div>
          )}

          {/* CTA Principal */}
          <div className="flex justify-center">
            <button
              onClick={handleGoHome}
              className="inline-flex items-center gap-3 px-10 py-4 bg-white text-purple-700 rounded-full font-semibold text-lg transition-all hover:scale-105 active:scale-95"
              style={{
                boxShadow: '0 0 40px rgba(255, 255, 255, 0.5), 0 8px 20px rgba(139, 92, 246, 0.4)',
              }}
            >
              {isEnglish ? 'Start' : 'Commencer'}
              <Home className="w-5 h-5" />
            </button>
          </div>
        </div>
      </div>

      <style>{`
        @keyframes halo-pulse {
          0%, 100% { opacity: 0.6; transform: translate(-50%, -50%) scale(1); }
          50% { opacity: 0.8; transform: translate(-50%, -50%) scale(1.05); }
        }
        
        @keyframes check-stroke-draw {
          from { stroke-dashoffset: 60; }
          to { stroke-dashoffset: 0; }
        }
      `}</style>
    </div>
  );
};

export default JourneyFinal;
